<template>
  <v-row no-gutters>
    <v-col cols="12">
      <sidebar-row border-bottom>
        <sidebar-measurements-list
          v-on="listeners"
          class="pb-4"
          :measurements="computedMeasurements"
          :loading="!measurementsFetched"
        />
      </sidebar-row>
    </v-col>
    <main-large-dialog
      v-if="isMainLargeDialogLoaded"
      :is-visible.sync="isMainLargeDialogVisible"
      v-bind="mainLargeDialogBind"
      :is-valid="isChartReady"
      @agree="measurementActionHandler"
    >
      <template #body>
        <v-col cols="12">
          <v-row no-gutters>
            <v-col cols="6">
              <data-input v-model="dateRange" v-bind="dateRangeInput" />
            </v-col>
          </v-row>
          <v-row v-if="isChartReady" class="pb-5">
            <v-col>
              <link-underlined @click="saveAsCSV" translation-path="sidebar.saveDataToCsv" />
            </v-col>
          </v-row>
          <chart-line
            v-bind="chartBind"
            ref="chart"
            :chart-title="currentMeasurement.verbose_name"
            :y-label="`[${currentMeasurement.measurement_unit}]`"
          />
        </v-col>
      </template>
    </main-large-dialog>
  </v-row>
</template>

<script>
import { call, get, sync } from 'vuex-pathify';
import { debounce } from 'lodash';

import ChartLine from '@/components/ChartLine';
import DataInput from '@/components/DataInput';
import SidebarMeasurementsList from '@/components/SidebarMeasurementsList';
import SidebarRow from '@/components/SidebarRow';
import LinkUnderlined from '@/components/LinkUnderlined';

export default {
  name: 'ScadaThreeMeasurements',
  components: {
    ChartLine,
    DataInput,
    MainLargeDialog: () => import('@/components/MainLargeDialog'),
    SidebarMeasurementsList,
    SidebarRow,
    LinkUnderlined,
  },
  props: {
    dataSource: {
      type: String,
      required: true,
    },
    featureId: {
      type: [Number, String],
      required: true,
    },
  },
  data: () => ({
    action: '',
    actionDialogsDict: {
      chart: 'MainLargeDialog',
    },
    dates: {},
    dateRange: [],
    chartDataLoading: false,
    currentChartData: [],
    currentChartLabels: [],
    currentMeasurement: undefined,
    isMainLargeDialogLoaded: false,
    isMainLargeDialogLoading: false,
    isMainLargeDialogVisible: false,
    measurementsFetched: false,
  }),
  computed: {
    scadaDataSourceName: get('admin/modulesMapping@scada3.measurements_datasource_name'),
    scadaMeasurementsPointAttribute: get('admin/modulesMapping@scada3.measurements_point_attribute'),
    scadaMeasurementsPoint: sync('scada/scadaThreeMeasurementsPoint'),
    capitalizeAction() {
      return this.$_capitalize(this.action);
    },
    chartBind() {
      return {
        labels: this.currentChartLabels,
        data: this.currentChartData,
        loading: this.chartDataLoading,
      };
    },
    isChartReady() {
      return !this.chartDataLoading && this.currentChartData && this.currentChartData.length > 0;
    },
    dateRanges() {
      const dict = {
        today: this.$dayjs(),
        tomorrow: this.$dayjs().add(1, 'd'),
        yesterday: this.$dayjs().subtract(1, 'd'),
        dayBeforeYesterday: this.$dayjs().subtract(2, 'd'),
        currentWeekStart: this.$dayjs().startOf('w'),
        currentWeekEnd: this.$dayjs().endOf('w'),
        currentMonthStart: this.$dayjs().startOf('M'),
        currentMonthEnd: this.$dayjs().endOf('M'),
        currentYearStart: this.$dayjs().startOf('y'),
        currentYearEnd: this.$dayjs().endOf('y'),
      };
      Object.keys(dict).map(key => {
        dict[key] = dict[key].format('YYYY-MM-DD');
      });
      return dict;
    },
    defaultDateRanges() {
      return [
        {
          name: 'today',
          text: this.$i18n.t('sidebar.zdmLightingControl.today'),
          value: [
            this.dateRanges.today,
            this.isMeasurementHourAggregated ? `${this.dateRanges.today}T23:59:59.999` : this.dateRanges.yesterday,
          ],
        },
        {
          name: 'yesterday',
          text: this.$i18n.t('sidebar.zdmLightingControl.yesterday'),
          value: [
            this.dateRanges.yesterday,
            this.isMeasurementHourAggregated ? `${this.dateRanges.yesterday}T23:59:59.999` : this.dateRanges.today,
          ],
        },
        {
          name: 'thisWeek',
          text: this.$i18n.t('sidebar.zdmLightingControl.thisWeek'),
          value: [
            this.dateRanges.currentWeekStart,
            this.isMeasurementHourAggregated
              ? `${this.dateRanges.currentWeekEnd}T23:59:59.999`
              : this.dateRanges.currentWeekEnd,
          ],
        },
        {
          name: 'thisMonth',
          text: this.$i18n.t('sidebar.zdmLightingControl.thisMonth'),
          value: [
            this.dateRanges.currentMonthStart,
            this.isMeasurementHourAggregated
              ? `${this.dateRanges.currentMonthEnd}T23:59:59.999`
              : this.dateRanges.currentMonthEnd,
          ],
        },
        {
          name: 'thisYear',
          text: this.$i18n.t('sidebar.zdmLightingControl.thisYear'),
          value: [
            this.dateRanges.currentYearStart,
            this.isMeasurementHourAggregated
              ? `${this.dateRanges.currentYearEnd}T23:59:59.999`
              : this.dateRanges.currentYearEnd,
          ],
        },
      ];
    },
    availableAggregateTypes() {
      return {
        series: {
          dataType: { name: 'daterange' },
          customDataInputType: {
            dataType: {
              name: 'datetime',
            },
          },
          additionalDataInputProps: {
            localeDateFormat: 'L',
          },
          localeDateFormat: 'L',
          datePickerProps: { type: 'date' },
          rangesShortcuts: ['today', 'yesterday', 'thisWeek', 'thisMonth', 'thisYear'],
        },
        2: {
          dataType: { name: 'daterange' },
          customDataInputType: {
            dataType: {
              name: 'datetime',
            },
          },
          additionalDataInputProps: {
            localeDateFormat: 'L',
            localeTimeFormat: 'H[h]',
          },
          localeDateFormat: 'L, H[h]',
          datePickerProps: { type: 'date' },
          rangesShortcuts: ['today', 'yesterday', 'thisWeek', 'thisMonth', 'thisYear'],
        },
        4: {
          dataType: { name: 'daterange' },
          customDataInputType: {
            dataType: {
              name: 'date',
            },
          },
          localeDateFormat: 'YYYY-MM-DD',
          datePickerProps: { type: 'date' },
          rangesShortcuts: ['today', 'yesterday', 'thisWeek', 'thisMonth', 'thisYear'],
        },
        8: {
          dataType: { name: 'daterange' },
          customDataInputType: {
            dataType: {
              name: 'date',
            },
          },
          localeDateFormat: 'YYYY-MM',
          datePickerProps: { type: 'month' },
          rangesShortcuts: ['thisMonth', 'thisYear'],
        },
        16: {
          dataType: { name: 'daterange' },
          customDataInputType: {
            dataType: {
              name: 'date',
            },
          },
          localeDateFormat: 'YYYY',
          datePickerProps: { type: 'year' },
          rangesShortcuts: ['thisYear'],
        },
      };
    },
    isMeasurementHourAggregated() {
      return this.currentMeasurement.report_period === '2' || this.currentMeasurement.measure_type === 'series';
    },
    dateRangeInput() {
      let currentAggregationData =
        this.currentMeasurement.measure_type === 'aggregate' && this.currentMeasurement.report_period
          ? this.availableAggregateTypes[this.currentMeasurement.report_period]
          : this.availableAggregateTypes.series;
      return {
        hideDetails: true,
        splitted: true,
        label: this.$i18n.t('sidebar.zdmLightingControl.dateRangeFromTo'),
        disabled: this.chartDataLoading,
        ...currentAggregationData,
        defaultRanges: this.defaultDateRanges.filter(range =>
          currentAggregationData.rangesShortcuts.includes(range.name)
        ),
      };
    },
    listeners() {
      const listeners = {
        ...this.$listeners,
      };
      for (const action of ['chart']) {
        listeners[action] = measurement => {
          this['measurementAction'](action, measurement);
        };
      }
      return listeners;
    },
    mainLargeDialogBind() {
      return {
        loading: this.isMainLargeDialogLoading,
        dialogWidth: '70%',
        maxHeight: '90vh',
        minBodyHeight: '300px',
        headerShadow: true,
        ...(this.isMainLargeDialogVisible
          ? {
              title: this.$i18n.t(`dialog.title.${this.action}Measurement`),
              agreeText: `dialog.agree${this.capitalizeAction}`,
            }
          : {}),
      };
    },
    computedMeasurements() {
      const contentButtonsDict = {
        series: [{ icon: 'mdi-chart-line', size: 'x-small', color: 'rgba(0,145,234,1)', name: 'chart' }],
        aggregate: [{ icon: 'mdi-chart-line', size: 'x-small', color: 'rgba(0,145,234,1)', name: 'chart' }],
        current: [],
      };
      return (
        this.scadaMeasurementsPoint?.map(measurement => {
          const { measure_type } = measurement;
          return {
            ...measurement,
            contentButtons: contentButtonsDict[measure_type],
          };
        }) || []
      );
    },
  },
  watch: {
    dateRange() {
      this.prepareChartData();
    },
    isMainLargeDialogVisible(nV) {
      if (nV) this.dateRange = [];
    },
  },
  mounted() {
    this.$root.$off('refreshScadaWidget');
    this.$root.$on('refreshScadaWidget', () => {
      this.getScadaMeasurementsPoint({ dataSource: this.dataSource, featureId: this.featureId });
    });
    this.$watch(
      vm => {
        const { dataSource, featureId } = vm;
        return { dataSource, featureId };
      },
      (nV, oV) => {
        if (oV) return;
        const { dataSource, featureId } = nV;
        if (dataSource && (featureId || featureId === 0)) {
          this.scadaMeasurementsPoint = [];
          this.getMeasurements(dataSource, featureId);
        }
      },
      { deep: true, immediate: true }
    );
  },
  created() {
    this.prepareChartData = debounce(this.prepareChartData, 1000);
  },
  methods: {
    getScadaMeasurementsPoint: call('scada/getScadaThreePoint'),
    getScadaMeasurementSeriesData: call('scada/getScadaThreeMeasurementSeriesData'),
    async getMeasurements(dataSource = this.dataSource, featureId = this.featureId) {
      try {
        this.measurementsFetched = false;
        await this.getScadaMeasurementsPoint({ dataSource, featureId });
      } finally {
        this.measurementsFetched = true;
      }
    },
    saveAsCSV() {
      let fileContent = `${this.currentMeasurement.verbose_name}\n${[
        this.$i18n.t('sidebar.measurementDate'),
        this.$i18n.t('sidebar.measurementValue'),
      ].join(';')}\n`;
      this.currentChartLabels?.forEach((label, index) => {
        fileContent += `${[label, this.currentChartData[index]].join(';')}\n`;
      });
      const file = new Blob([fileContent], { type: 'text/csv;charset=utf-8;' });
      this.$_saveFile(URL.createObjectURL(file), `${this.currentMeasurement.verbose_name}.csv`);
    },
    async handlerChart() {
      this.$refs.chart.downloadChart(this.currentMeasurement?.verbose_name);
    },
    async prepareChartData() {
      if (this.dateRange?.length !== 2) {
        return;
      }
      this.chartDataLoading = true;
      let [dateStart, dateEnd] = this.dateRange;
      if (this.isMeasurementHourAggregated) dateEnd = this.$dayjs(dateEnd).format('YYYY-MM-DDTHH:mm[:59]');
      const payload = {
        since: dateStart,
        until: dateEnd,
      };
      const data = {
        payload,
        measurementId: this.currentMeasurement.id,
      };
      try {
        const stats = await this.getScadaMeasurementSeriesData(data);
        this.currentChartData = stats.map(stat => stat.value);
        this.currentChartLabels = stats.map(stat => {
          const date = this.$dayjs(stat.date).format('L');
          const time = this.$dayjs(stat.date).format('LT');
          return `${date}, ${time}`;
        });
        this.chartDataLoading = false;
      } catch {
        this.chartDataLoading = false;
      }
    },
    async measurementAction(action, measurement) {
      this.action = action;
      this.currentMeasurement = measurement || undefined;
      this.dateRange = [];
      this.currentChartData = [];
      this.currentChartLabels = [];
      const dialogType = this.actionDialogsDict[action];
      this[`is${dialogType}Loaded`] = true;
      this[`is${dialogType}Visible`] = true;
    },
    async measurementActionHandler() {
      const dialogType = this.actionDialogsDict[this.action];
      this[`is${dialogType}Loading`] = true;
      try {
        await this[`handler${this.capitalizeAction}`](dialogType);
      } finally {
        this[`is${dialogType}Loading`] = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-chip--label {
    border-radius: 12px !important;
  }
  .custom-year-picker {
    .v-date-picker-title__date {
      display: none !important;
    }
  }
  .custom-year-picker__novalue {
    .v-date-picker-years .active.primary--text {
      color: #000 !important;
    }
    .v-date-picker-years .active {
      font-size: unset !important;
    }
  }
}
.date-range-chip {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
}
</style>
